import type { DragEndEvent } from '@dnd-kit/core';
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AccessLevel,
  colors,
  darkThemeSelector,
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPopover,
  fade,
  palette,
  styled,
  UtilityControl,
} from '@meterup/atto';
import { memo, useCallback, useMemo, useState } from 'react';

import type { SharedColumnDef } from '../createColumnBuilder';
import type { AutoCollectionStateInterface } from '../utils';

interface TableColumnsVisibilityProps<RowType = any> {
  columns: ColumnDef<RowType, any>[];
  columnVisibility: Record<string, boolean>;
  setColumnVisibility: (value: Record<string, boolean>) => void;
  columnOrder?: string[];
  onResetColumnOrder?: () => void;
  defaultColumnOrder?: string[];
}

// New enhanced interface that works with TableState
interface TableColumnsEnhancedProps<RowType = any> {
  columns: SharedColumnDef<RowType, any>[];
  tableState: AutoCollectionStateInterface<RowType>;
}

export const DEFAULT_COLUMN_GROUP_NAME = 'Columns';
export const OPERATOR_COLUMN_GROUP_NAME = 'Operator';

const TableColumnsDraggableHandle = styled(UtilityControl, {
  cursor: 'grab',

  variants: {
    isDragging: {
      true: {
        cursor: 'grabbing',
      },
      false: {},
    },
  },
});
TableColumnsDraggableHandle.displayName = 'TableColumnsDraggableHandle';

const TableColumnsDraggableCheckbox = styled(DropdownMenuCheckboxItem, {
  width: '100%',
  pointerEvents: '$$tableColumnsDraggableCheckboxPointerEvents',
});
TableColumnsDraggableCheckbox.displayName = 'TableColumnsDraggableCheckbox';

const TableColumnsDraggableContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  gap: '$4',
  paddingRight: '$4',
  borderRadius: '$6',
  userSelect: 'none',
  cursor: 'grab',

  variants: {
    isDragging: {
      true: {
        opacity: 0.8,
        zIndex: 2,
        background: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,
        cursor: 'grabbing',

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
        },
      },
      false: {
        zIndex: 1,
        opacity: 1,
      },
    },
  },
});
TableColumnsDraggableContainer.displayName = 'TableColumnsDraggableContainer';

const TableColumnsGroup = styled(DropdownMenuGroup, {
  variants: {
    isDragging: {
      true: {
        position: 'relative',
        $$tableColumnsDraggableCheckboxPointerEvents: 'none',

        '&::before': {
          content: '',
          position: 'absolute',
          inset: '$4',
          background: fade(palette.bgBrandLight, 0.2),
          strokeAll: fade(palette.strokeBrandLight, 0.3),
          borderRadius: '$6',
          pointerEvents: 'none',
          zIndex: 1,

          [darkThemeSelector]: {
            background: fade(palette.bgBrandDark, 0.2),
            strokeAll: fade(palette.strokeBrandDark, 0.3),
          },
        },
      },
      false: {
        $$tableColumnsDraggableCheckboxPointerEvents: 'auto',
      },
    },
  },
});
TableColumnsGroup.displayName = 'TableColumnsGroup';

const TableColumnsDraggable = memo(
  ({
    column,
    columnVisibility,
    onVisibilityChange,
    isDragging,
  }: {
    column: ColumnDef<any, any>;
    columnVisibility: Record<string, boolean>;
    onVisibilityChange: (columnId: string, checked: boolean) => void;
    isDragging: boolean;
  }) => {
    const isDraggable = !column.meta?.fixedPosition;
    const isAlwaysVisible = column.meta?.alwaysVisible === true;

    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: column.id!,
      disabled: !isDraggable,
      animateLayoutChanges: () => false, // Disable layout animations to prevent flicker
      transition: {
        duration: 150,
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      },
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <TableColumnsDraggableContainer ref={setNodeRef} style={style} isDragging={isDragging}>
        <TableColumnsDraggableCheckbox
          checked={isAlwaysVisible ? true : columnVisibility[column.id!] ?? true}
          disabled={isAlwaysVisible}
          accessLevel={column.meta?.accessLevel}
          onCheckedChange={(checked) => {
            if (!isAlwaysVisible) {
              onVisibilityChange(column.id!, checked);
            }
          }}
          onSelect={(event: Event) => {
            event.preventDefault();
          }}
        >
          {typeof column.header === 'string' ? column.header : column.id}
        </TableColumnsDraggableCheckbox>
        {isDraggable && (
          <TableColumnsDraggableHandle
            {...attributes}
            {...listeners}
            active={isDragging}
            icon="slide-vertical"
            isDragging={isDragging}
            label="Reorder"
            variant={isDragging ? 'brand' : 'neutral'}
          />
        )}
      </TableColumnsDraggableContainer>
    );
  },
);

const TableColumnsInner = memo(
  ({
    columns,
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    onResetColumnOrder,
    defaultColumnOrder,
  }: TableColumnsVisibilityProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const filteredColumns = useMemo(
      () =>
        columns.filter(
          (column) => column.id && column.meta?.accessLevel !== null && column.meta?.group !== null,
        ),
      [columns],
    );

    const groupedColumns = useMemo(() => {
      const groups = new Map<string, ColumnDef<any, any>[]>();

      for (const col of filteredColumns) {
        const groupName =
          col.meta?.group ??
          (col.meta?.accessLevel === AccessLevel.Internal
            ? OPERATOR_COLUMN_GROUP_NAME
            : DEFAULT_COLUMN_GROUP_NAME);

        let arr = groups.get(groupName);
        if (!arr) {
          arr = [];
          groups.set(groupName, arr);
        }

        arr.push(col);
      }

      return groups;
    }, [filteredColumns]);

    const sortedColumnGroups = useMemo(() => {
      if (!groupedColumns || groupedColumns.size === 0) return [];

      return Array.from(groupedColumns.entries()).sort(([groupA], [groupB]) => {
        if (groupA === DEFAULT_COLUMN_GROUP_NAME && groupB !== DEFAULT_COLUMN_GROUP_NAME) return -1;
        if (groupA !== DEFAULT_COLUMN_GROUP_NAME && groupB === DEFAULT_COLUMN_GROUP_NAME) return 1;

        if (groupA === OPERATOR_COLUMN_GROUP_NAME && groupB !== OPERATOR_COLUMN_GROUP_NAME)
          return 1;
        if (groupA !== OPERATOR_COLUMN_GROUP_NAME && groupB === OPERATOR_COLUMN_GROUP_NAME)
          return -1;

        return groupA.localeCompare(groupB);
      });
    }, [groupedColumns]);

    const sortedColumnsInGroups = useMemo(() => {
      if (!columnOrder) return sortedColumnGroups;

      return sortedColumnGroups.map(([groupName, groupColumns]) => {
        const sortedGroupColumns = groupColumns.sort((a, b) => {
          const aIndex = columnOrder.indexOf(a.id!);
          const bIndex = columnOrder.indexOf(b.id!);

          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }

          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;

          return 0;
        });

        return [groupName, sortedGroupColumns] as [string, ColumnDef<any, any>[]];
      });
    }, [sortedColumnGroups, columnOrder]);

    const handleVisibilityChange = useCallback(
      (columnId: string, checked: boolean) => {
        const newVisibility = {
          ...columnVisibility,
          [columnId]: checked,
        };
        setColumnVisibility(newVisibility);
      },
      [columnVisibility, setColumnVisibility],
    );

    const handleResetColumnOrder = useCallback(() => {
      onResetColumnOrder?.();
    }, [onResetColumnOrder]);

    const isColumnOrderDefault = useMemo(() => {
      if (!defaultColumnOrder || !columnOrder) return true;

      if (defaultColumnOrder.length !== columnOrder.length) return false;

      return defaultColumnOrder.every((id, index) => id === columnOrder[index]);
    }, [defaultColumnOrder, columnOrder]);

    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuButton
          arrangement="hidden-label"
          icon="columns"
          variant="secondary"
          size="small"
        >
          Manage columns
        </DropdownMenuButton>
        <DropdownMenuPopover
          align="start"
          onCloseAutoFocus={(event: Event) => {
            event.preventDefault();
          }}
        >
          {sortedColumnsInGroups.map(([groupName, groupColumns]) => (
            <DropdownMenuGroup key={groupName} label={groupName}>
              {groupColumns.map((column) => {
                if (!column.id) return null;
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={columnVisibility[column.id] ?? true}
                    accessLevel={column.meta?.accessLevel}
                    onCheckedChange={(checked) => {
                      handleVisibilityChange(column.id!, checked);
                    }}
                    onSelect={(event: Event) => {
                      event.preventDefault();
                    }}
                  >
                    {typeof column.header === 'string' ? column.header : column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuGroup>
          ))}
          {onResetColumnOrder && (
            <DropdownMenuGroup label="Controls">
              <DropdownMenuItem
                onClick={handleResetColumnOrder}
                disabled={isColumnOrderDefault}
                icon="reset"
              >
                Reset order
              </DropdownMenuItem>
            </DropdownMenuGroup>
          )}
        </DropdownMenuPopover>
      </DropdownMenu>
    );
  },
);

// Enhanced component that works with TableState
function TableColumnsEnhancedComponent<D>({ columns, tableState }: TableColumnsEnhancedProps<D>) {
  const [isOpen, setIsOpen] = useState(false);
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [tempColumnOrder, setTempColumnOrder] = useState<string[] | null>(null);

  const filteredColumns = useMemo(() => {
    if (!columns || !Array.isArray(columns) || columns.length === 0) return [];

    return columns.filter(
      (column) =>
        column && column.id && column.meta?.accessLevel !== null && column.meta?.group !== null,
    );
  }, [columns]);

  const groupedColumns = useMemo(() => {
    if (!filteredColumns || filteredColumns.length === 0)
      return new Map<string, ColumnDef<any, any>[]>();

    const groups = new Map<string, ColumnDef<any, any>[]>();

    for (const col of filteredColumns) {
      if (!col || !col.id) continue;

      const groupName =
        col.meta?.group ??
        (col.meta?.accessLevel === AccessLevel.Internal
          ? OPERATOR_COLUMN_GROUP_NAME
          : DEFAULT_COLUMN_GROUP_NAME);

      let arr = groups.get(groupName);
      if (!arr) {
        arr = [];
        groups.set(groupName, arr);
      }

      arr.push(col);
    }

    return groups;
  }, [filteredColumns]);

  const sortedColumnGroups = useMemo(() => {
    if (!groupedColumns || groupedColumns.size === 0) return [];

    return Array.from(groupedColumns.entries()).sort(([groupA], [groupB]) => {
      if (groupA === DEFAULT_COLUMN_GROUP_NAME && groupB !== DEFAULT_COLUMN_GROUP_NAME) return -1;
      if (groupA !== DEFAULT_COLUMN_GROUP_NAME && groupB === DEFAULT_COLUMN_GROUP_NAME) return 1;

      if (groupA === OPERATOR_COLUMN_GROUP_NAME && groupB !== OPERATOR_COLUMN_GROUP_NAME) return 1;
      if (groupA !== OPERATOR_COLUMN_GROUP_NAME && groupB === OPERATOR_COLUMN_GROUP_NAME) return -1;

      return groupA.localeCompare(groupB);
    });
  }, [groupedColumns]);

  const sortedColumnsInGroups = useMemo(() => {
    const orderToUse = tempColumnOrder || tableState.columnOrder;
    if (!orderToUse || !Array.isArray(orderToUse)) return sortedColumnGroups;

    return sortedColumnGroups.map(([groupName, groupColumns]) => {
      if (!groupColumns || groupColumns.length === 0) {
        return [groupName, groupColumns] as [string, ColumnDef<any, any>[]];
      }

      const sortedGroupColumns = groupColumns.sort((a, b) => {
        if (!a.id || !b.id) return 0;

        const aFixed = a.meta?.fixedPosition;
        const bFixed = b.meta?.fixedPosition;
        const aIndex = orderToUse.indexOf(a.id);
        const bIndex = orderToUse.indexOf(b.id);

        // start first, then middle (as-in / null), then end
        if (aFixed === 'start' && bFixed !== 'start') return -1;
        if (aFixed !== 'start' && bFixed === 'start') return 1;
        if (aFixed === 'end' && bFixed !== 'end') return 1;
        if (aFixed !== 'end' && bFixed === 'end') return -1;

        // Same tier: use column order
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return 0;
      });

      return [groupName, sortedGroupColumns] as [string, ColumnDef<any, any>[]];
    });
  }, [sortedColumnGroups, tableState.columnOrder, tempColumnOrder]);

  const findColumnGroup = useCallback(
    (columnId: string): string | null => {
      if (!columnId || !sortedColumnsInGroups) return null;

      for (const [groupName, groupColumns] of sortedColumnsInGroups) {
        if (groupColumns?.some((col) => col?.id === columnId)) {
          return groupName;
        }
      }
      return null;
    },
    [sortedColumnsInGroups],
  );

  const handleVisibilityChange = useCallback(
    (columnId: string, checked: boolean) => {
      const newVisibility = {
        ...tableState.columnVisibility,
        [columnId]: checked,
      };
      tableState.setColumnVisibility(newVisibility);
    },
    [tableState],
  );

  const handleColumnReorder = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) return;

      const activeColumnId = active.id as string;
      const overColumnId = over.id as string;

      const activeGroup = findColumnGroup(activeColumnId);
      const overGroup = findColumnGroup(overColumnId);

      // Only allow reordering within the same group
      if (activeGroup !== overGroup) {
        return;
      }

      const currentOrder = tableState.columnOrder;
      if (!currentOrder || !Array.isArray(currentOrder)) return;

      const activeIndex = currentOrder.indexOf(activeColumnId);
      const overIndex = currentOrder.indexOf(overColumnId);

      if (activeIndex === -1 || overIndex === -1) return;

      // Check if either column is fixed
      const activeColumn = columns.find((col) => col.id === activeColumnId);
      const overColumn = columns.find((col) => col.id === overColumnId);

      if (activeColumn?.meta?.fixedPosition || overColumn?.meta?.fixedPosition) {
        return; // Prevent reordering of fixed columns
      }

      // Create new order array
      const newOrder = [...currentOrder];
      const [movedColumn] = newOrder.splice(activeIndex, 1);
      newOrder.splice(overIndex, 0, movedColumn);

      tableState.setColumnOrder(newOrder);
    },
    [tableState, findColumnGroup, columns],
  );

  const hasColumnOrder = useMemo(
    () => tempColumnOrder && tempColumnOrder.length > 0,
    [tempColumnOrder],
  );

  const hasColumnVisibility = useMemo(
    () =>
      sortedColumnsInGroups &&
      sortedColumnsInGroups.length > 0 &&
      sortedColumnsInGroups.some(
        ([, groupColumns]) => groupColumns && groupColumns.some((column) => column && column.id),
      ),
    [sortedColumnsInGroups],
  );

  const hasColumnManagement = useMemo(
    () => hasColumnOrder || hasColumnVisibility,
    [hasColumnOrder, hasColumnVisibility],
  );

  const lockHorizontalMovement = useCallback((args: any) => {
    const { transform, active } = args;

    // Safety check - if no active element, return original transform
    if (!active?.id) {
      return transform;
    }

    // Lock horizontal movement - columns should only move up/down
    const constrainedX = 0;

    // Find the dragged column's group container to get real boundaries
    const activeElement = document.querySelector(`[data-dnd-kit-id="${active.id}"]`);
    const groupContainer = activeElement?.closest('[role="group"]');

    if (groupContainer) {
      const groupRect = groupContainer.getBoundingClientRect();

      // Use actual group height for vertical constraints
      const constrainedY = Math.max(
        -groupRect.height + 50, // Allow movement up to near the top
        Math.min(transform.y, groupRect.height - 50), // Allow movement down to near the bottom
      );

      return {
        ...transform,
        x: constrainedX,
        y: constrainedY,
      };
    }

    // Fallback if group container not found
    return {
      ...transform,
      x: constrainedX,
      y: transform.y,
    };
  }, []);

  const allColumnIds = useMemo(() => {
    if (!sortedColumnsInGroups?.length) return [];

    return sortedColumnsInGroups.flatMap(
      ([, groupColumns]) =>
        groupColumns?.map((col) => col?.id).filter((id): id is string => Boolean(id)) ?? [],
    );
  }, [sortedColumnsInGroups]);

  return hasColumnManagement ? (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuButton
        arrangement="hidden-label"
        icon="columns"
        variant="secondary"
        size="small"
      >
        Manage columns
      </DropdownMenuButton>
      <DropdownMenuPopover
        align="start"
        onCloseAutoFocus={(event: Event) => {
          event.preventDefault();
        }}
      >
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[lockHorizontalMovement]}
          onDragStart={(event) => setDraggedColumnId(event.active.id as string)}
          onDragEnd={(event) => {
            if (event.active && event.over && event.active.id !== event.over.id) {
              const activeColumnId = event.active.id as string;
              const overColumnId = event.over.id as string;

              const activeGroup = findColumnGroup(activeColumnId);
              const overGroup = findColumnGroup(overColumnId);

              if (activeGroup === overGroup) {
                // Immediately set temporary order to prevent flicker
                const currentOrder = tableState.columnOrder;
                if (currentOrder && Array.isArray(currentOrder)) {
                  const activeIndex = currentOrder.indexOf(activeColumnId);
                  const overIndex = currentOrder.indexOf(overColumnId);

                  if (activeIndex !== -1 && overIndex !== -1) {
                    const newOrder = [...currentOrder];
                    const [movedColumn] = newOrder.splice(activeIndex, 1);
                    newOrder.splice(overIndex, 0, movedColumn);
                    setTempColumnOrder(newOrder);
                  }
                }

                handleColumnReorder(event);
              } else {
                // Cross-group reorder blocked, do nothing
              }
            }

            setDraggedColumnId(null);

            // Clear temporary order after state update
            setTimeout(() => {
              setTempColumnOrder(null);
            }, 100);
          }}
        >
          <SortableContext items={allColumnIds} strategy={verticalListSortingStrategy}>
            {hasColumnVisibility &&
              sortedColumnsInGroups.map(([groupName, groupColumns]) => {
                const isGroupBeingDragged = Boolean(
                  draggedColumnId && groupColumns.some((col) => col.id === draggedColumnId),
                );

                return (
                  <TableColumnsGroup
                    key={groupName}
                    label={groupName}
                    isDragging={isGroupBeingDragged}
                  >
                    {groupColumns.map((column) => {
                      if (!column.id) return null;
                      return (
                        <TableColumnsDraggable
                          key={column.id}
                          column={column}
                          columnVisibility={tableState.columnVisibility as VisibilityState}
                          onVisibilityChange={handleVisibilityChange}
                          isDragging={draggedColumnId === column.id}
                        />
                      );
                    })}
                  </TableColumnsGroup>
                );
              })}
          </SortableContext>
        </DndContext>
        {(hasColumnOrder || hasColumnVisibility) && (
          <DropdownMenuGroup label="Reset columns">
            <DropdownMenuItem
              onClick={tableState.resetColumnOrder}
              disabled={tableState.isColumnOrderDefault}
              icon="columns"
            >
              Order
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={tableState.resetColumnVisibility}
              disabled={tableState.isColumnVisibilityDefault}
              icon="eye-open"
            >
              Visibility
            </DropdownMenuItem>
          </DropdownMenuGroup>
        )}
      </DropdownMenuPopover>
    </DropdownMenu>
  ) : null;
}

export const TableColumns = memo(TableColumnsInner);

export const TableColumnsEnhanced = memo(
  TableColumnsEnhancedComponent,
  // This typecast is to make the generics propagate properly
) as typeof TableColumnsEnhancedComponent;
